import { useState, useEffect, useCallback, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  Sparkles,
  Loader2,
  Copy,
  CheckCircle,
  XCircle,
  FileText,
  Mail,
  MessageSquare,
  BookOpen,
  Target,
  Network,
  CalendarDays,
  Swords,
  TrendingUp,
  Megaphone,
  Share2,
  Users,
  Globe,
  Wand2,
  AlertTriangle,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  marketingApi,
  type AIContentType,
  type AIContentItem,
  type AIBrandContext,
} from "@/services/api/marketing";
import { PortalMarkdown } from "@/components/portal/shared/PortalMarkdown";
import { useAuth } from "@/contexts/AuthContext";

export const Route = createFileRoute("/portal/ai-content/")({
  component: AIContentGeneratePage,
});

const CONTENT_TYPES: {
  value: AIContentType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  category: string;
}[] = [
  // Create
  {
    value: "blog_post",
    label: "Blog Post",
    icon: FileText,
    description: "SEO-optimised blog post (1200-2000 words)",
    category: "Create",
  },
  {
    value: "case_study",
    label: "Case Study",
    icon: BookOpen,
    description: "Customer success story with metrics",
    category: "Create",
  },
  {
    value: "email_sequence",
    label: "Email Sequence",
    icon: Mail,
    description: "Nurture email sequence (3-5 emails)",
    category: "Create",
  },
  {
    value: "social_post",
    label: "Social Post",
    icon: MessageSquare,
    description: "Platform-specific social media post",
    category: "Create",
  },
  // Plan
  {
    value: "content_brief",
    label: "Content Brief",
    icon: Target,
    description: "Structured brief with hook, messaging, and outline",
    category: "Plan",
  },
  {
    value: "topic_cluster",
    label: "Topic Cluster",
    icon: Network,
    description: "Pillar topic with 8-12 supporting content pieces",
    category: "Plan",
  },
  {
    value: "content_calendar",
    label: "Content Calendar",
    icon: CalendarDays,
    description: "Multi-week themed content schedule",
    category: "Plan",
  },
  // Research
  {
    value: "competitive_analysis",
    label: "Competitive Analysis",
    icon: Swords,
    description: "SWOT framework with competitor profiles (uses web search)",
    category: "Research",
  },
  {
    value: "market_research",
    label: "Market Research",
    icon: TrendingUp,
    description: "Market trends, segments, and recommendations (uses web search)",
    category: "Research",
  },
  {
    value: "audience_insight",
    label: "Audience Insight",
    icon: Users,
    description: "Buyer persona profile with pain points and messaging (uses web search)",
    category: "Research",
  },
  // Campaign
  {
    value: "campaign_plan",
    label: "Campaign Plan",
    icon: Megaphone,
    description: "Multi-channel campaign with KPIs and timeline",
    category: "Campaign",
  },
  {
    value: "social_calendar",
    label: "Social Calendar",
    icon: Share2,
    description: "Multi-week social media posting schedule",
    category: "Campaign",
  },
];

const RESEARCH_FORCED_TYPES: AIContentType[] = [
  "competitive_analysis",
  "market_research",
  "audience_insight",
];

const PLATFORM_OPTIONS = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter", label: "Twitter/X" },
  { value: "facebook", label: "Facebook" },
];

const SEQUENCE_TYPES = [
  { value: "nurture", label: "Nurture" },
  { value: "onboarding", label: "Onboarding" },
  { value: "re-engagement", label: "Re-engagement" },
];

function AIContentGeneratePage() {
  const { token } = useAuth();

  // Form state
  const [contentType, setContentType] = useState<AIContentType>("blog_post");
  const [prompt, setPrompt] = useState("");
  const [title, setTitle] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [targetKeywords, setTargetKeywords] = useState("");
  const [tone, setTone] = useState("");
  const [wordCountTarget, setWordCountTarget] = useState("");
  const [platform, setPlatform] = useState("linkedin");
  const [sequenceType, setSequenceType] = useState("nurture");
  const [numEmails, setNumEmails] = useState("3");
  const [includeResearch, setIncludeResearch] = useState(false);
  // Strategist fields
  const [contentGoal, setContentGoal] = useState("");
  const [pillarTopic, setPillarTopic] = useState("");
  const [durationWeeks, setDurationWeeks] = useState("");
  // Researcher fields
  const [competitorNames, setCompetitorNames] = useState("");
  const [researchRegion, setResearchRegion] = useState("");
  const [personaRole, setPersonaRole] = useState("");
  const [industryVertical, setIndustryVertical] = useState("");
  // Campaign fields
  const [channels, setChannels] = useState("");
  const [budgetRange, setBudgetRange] = useState("");
  const [platforms, setPlatforms] = useState("");

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] =
    useState<AIContentItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Improve state
  const [showImprove, setShowImprove] = useState(false);
  const [improveInstructions, setImproveInstructions] = useState("");
  const [isImproving, setIsImproving] = useState(false);

  // Brand context
  const [brandContext, setBrandContext] = useState<AIBrandContext | null>(null);
  const [brandLoading, setBrandLoading] = useState(false);

  // Brand profile is "configured" when at least one key field is set
  const hasBrandProfile = (() => {
    const org = brandContext?.org_brand_profile;
    if (!org || typeof org !== "object") return false;
    return !!(org.company_name || org.brand_voice || org.value_proposition);
  })();

  // Auto-enable research for forced types
  useEffect(() => {
    if (RESEARCH_FORCED_TYPES.includes(contentType)) {
      setIncludeResearch(true);
    }
  }, [contentType]);

  // Load brand context on mount
  useEffect(() => {
    if (!token) return;
    setBrandLoading(true);
    marketingApi
      .getAIBrandContext(token)
      .then(setBrandContext)
      .catch(() => {
        /* Brand context is optional for display */
      })
      .finally(() => setBrandLoading(false));
  }, [token]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const pollStatus = useCallback(
    (contentId: string) => {
      if (!token) return;
      pollRef.current = setInterval(async () => {
        try {
          const status = await marketingApi.getAIContentStatus(
            token,
            contentId,
          );
          if (status.status === "completed" || status.status === "failed") {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setGeneratedContent(status);
            setIsGenerating(false);
            setIsImproving(false);
            if (status.status === "completed") {
              toast.success("Content generated successfully");
            } else {
              setError(status.error_message || "Generation failed");
              toast.error("Content generation failed");
            }
          }
        } catch {
          // Keep polling on transient errors
        }
      }, 3000);
    },
    [token],
  );

  const handleGenerate = async () => {
    if (!token || !prompt.trim()) return;

    setIsGenerating(true);
    setGeneratedContent(null);
    setError(null);
    setShowImprove(false);

    try {
      const request: Parameters<typeof marketingApi.generateAIContent>[1] = {
        content_type: contentType,
        prompt: prompt.trim(),
        title: title.trim() || undefined,
        target_audience: targetAudience.trim() || undefined,
        target_keywords: targetKeywords.trim()
          ? targetKeywords.split(",").map((k) => k.trim())
          : undefined,
        tone: tone.trim() || undefined,
        word_count_target: wordCountTarget
          ? parseInt(wordCountTarget)
          : undefined,
        include_research: includeResearch || undefined,
      };

      // Type-specific fields
      if (contentType === "social_post") {
        request.platform = platform;
      }
      if (contentType === "email_sequence") {
        request.sequence_type = sequenceType;
        request.num_emails = parseInt(numEmails);
      }
      if (
        contentType === "content_brief" ||
        contentType === "topic_cluster" ||
        contentType === "content_calendar"
      ) {
        if (contentGoal.trim()) request.content_goal = contentGoal.trim();
      }
      if (contentType === "topic_cluster") {
        if (pillarTopic.trim()) request.pillar_topic = pillarTopic.trim();
      }
      if (
        contentType === "content_calendar" ||
        contentType === "social_calendar"
      ) {
        if (durationWeeks) request.duration_weeks = parseInt(durationWeeks);
      }
      if (contentType === "competitive_analysis") {
        if (competitorNames.trim()) {
          request.competitor_names = competitorNames
            .split(",")
            .map((c) => c.trim());
        }
      }
      if (contentType === "market_research") {
        if (researchRegion.trim())
          request.research_region = researchRegion.trim();
      }
      if (contentType === "audience_insight") {
        if (personaRole.trim())
          request.persona_role = personaRole.trim();
        if (industryVertical.trim())
          request.industry_vertical = industryVertical.trim();
      }
      if (contentType === "campaign_plan") {
        if (channels.trim()) {
          request.channels = channels.split(",").map((c) => c.trim());
        }
        if (budgetRange.trim()) request.budget_range = budgetRange.trim();
      }
      if (contentType === "social_calendar") {
        if (platforms.trim()) {
          request.platforms = platforms.split(",").map((p) => p.trim());
        }
      }

      const response = await marketingApi.generateAIContent(token, request);
      pollStatus(response.content_id);
    } catch (err) {
      setIsGenerating(false);
      const msg =
        err instanceof Error ? err.message : "Failed to start generation";
      setError(msg);
      toast.error(msg);
    }
  };

  const handleImprove = async () => {
    if (!token || !generatedContent || !improveInstructions.trim()) return;

    setIsImproving(true);
    setError(null);

    try {
      const response = await marketingApi.improveAIContent(
        token,
        generatedContent.id,
        {
          instructions: improveInstructions.trim(),
          include_research: includeResearch || undefined,
        },
      );
      setShowImprove(false);
      setImproveInstructions("");
      setGeneratedContent(null);
      setIsGenerating(true);
      pollStatus(response.content_id);
      toast.success("Improvement started");
    } catch (err) {
      setIsImproving(false);
      const msg =
        err instanceof Error ? err.message : "Failed to start improvement";
      toast.error(msg);
    }
  };

  const handleCopy = () => {
    if (!generatedContent?.rendered_content) return;
    navigator.clipboard.writeText(generatedContent.rendered_content);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedType = CONTENT_TYPES.find((t) => t.value === contentType);
  const isResearchForced = RESEARCH_FORCED_TYPES.includes(contentType);

  // Group content types by category
  const categories = [...new Set(CONTENT_TYPES.map((t) => t.category))];

  return (
    <>
      <Helmet>
        <title>AI Content | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Generate Content"
          description="Create content, plan strategies, research markets, and build campaigns with AI."
          icon={Sparkles}
          eyebrow="AI Content"
          gradient
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left: Generation Form */}
          <div className="space-y-4 lg:col-span-2">
            {/* Content Type Selector */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Content Type</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {categories.map((category) => (
                  <div key={category}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {category}
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                      {CONTENT_TYPES.filter((t) => t.category === category).map(
                        (type) => {
                          const Icon = type.icon;
                          const isSelected = contentType === type.value;
                          return (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => setContentType(type.value)}
                              className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 text-center transition-colors ${
                                isSelected
                                  ? "border-purple-500 bg-purple-50 text-purple-700"
                                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                              }`}
                            >
                              <Icon
                                className={`h-5 w-5 ${isSelected ? "text-purple-500" : "text-slate-400"}`}
                              />
                              <span className="text-xs font-medium">
                                {type.label}
                              </span>
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                ))}
                {selectedType && (
                  <p className="text-sm text-slate-500">
                    {selectedType.description}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Prompt + Parameters */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Brief</CardTitle>
                <CardDescription>
                  Describe what you want to generate. Be specific about the
                  topic, angle, and goals.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Title (optional)</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. 5 Ways Cloud ERP Transforms Financial Services"
                    disabled={isGenerating}
                  />
                </div>

                <div>
                  <Label htmlFor="prompt">Prompt *</Label>
                  <Textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the content you want to generate..."
                    rows={4}
                    disabled={isGenerating}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="audience">Target Audience</Label>
                    <Input
                      id="audience"
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      placeholder="e.g. CFOs at mid-market companies"
                      disabled={isGenerating}
                    />
                  </div>
                  <div>
                    <Label htmlFor="tone">Tone</Label>
                    <Input
                      id="tone"
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                      placeholder="e.g. professional, conversational"
                      disabled={isGenerating}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="keywords">
                      Keywords (comma-separated)
                    </Label>
                    <Input
                      id="keywords"
                      value={targetKeywords}
                      onChange={(e) => setTargetKeywords(e.target.value)}
                      placeholder="e.g. cloud ERP, digital transformation"
                      disabled={isGenerating}
                    />
                  </div>
                  {(contentType === "blog_post" ||
                    contentType === "case_study") && (
                    <div>
                      <Label htmlFor="wordCount">Word Count Target</Label>
                      <Input
                        id="wordCount"
                        type="number"
                        value={wordCountTarget}
                        onChange={(e) => setWordCountTarget(e.target.value)}
                        placeholder="e.g. 1500"
                        disabled={isGenerating}
                      />
                    </div>
                  )}
                </div>

                {/* Research toggle */}
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label
                      htmlFor="research-toggle"
                      className="flex items-center gap-2 text-sm font-medium"
                    >
                      <Globe className="h-4 w-4 text-blue-500" />
                      Web Research
                    </Label>
                    <p className="text-xs text-slate-500">
                      {isResearchForced
                        ? "Always enabled for this content type"
                        : "Uses AI web search for real-world data and citations"}
                    </p>
                  </div>
                  <Switch
                    id="research-toggle"
                    checked={includeResearch}
                    onCheckedChange={setIncludeResearch}
                    disabled={isGenerating || isResearchForced}
                  />
                </div>

                {/* Social post specific */}
                {contentType === "social_post" && (
                  <div>
                    <Label>Platform</Label>
                    <Select
                      value={platform}
                      onValueChange={setPlatform}
                      disabled={isGenerating}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PLATFORM_OPTIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Email sequence specific */}
                {contentType === "email_sequence" && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Sequence Type</Label>
                      <Select
                        value={sequenceType}
                        onValueChange={setSequenceType}
                        disabled={isGenerating}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEQUENCE_TYPES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="numEmails">Number of Emails</Label>
                      <Input
                        id="numEmails"
                        type="number"
                        min={2}
                        max={7}
                        value={numEmails}
                        onChange={(e) => setNumEmails(e.target.value)}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                )}

                {/* Strategist fields */}
                {(contentType === "content_brief" ||
                  contentType === "topic_cluster" ||
                  contentType === "content_calendar") && (
                  <div>
                    <Label htmlFor="contentGoal">Content Goal</Label>
                    <Input
                      id="contentGoal"
                      value={contentGoal}
                      onChange={(e) => setContentGoal(e.target.value)}
                      placeholder="e.g. increase signups, thought leadership"
                      disabled={isGenerating}
                    />
                  </div>
                )}

                {contentType === "topic_cluster" && (
                  <div>
                    <Label htmlFor="pillarTopic">Pillar Topic</Label>
                    <Input
                      id="pillarTopic"
                      value={pillarTopic}
                      onChange={(e) => setPillarTopic(e.target.value)}
                      placeholder="e.g. Cloud ERP for Financial Services"
                      disabled={isGenerating}
                    />
                  </div>
                )}

                {(contentType === "content_calendar" ||
                  contentType === "social_calendar") && (
                  <div>
                    <Label htmlFor="durationWeeks">Duration (weeks)</Label>
                    <Input
                      id="durationWeeks"
                      type="number"
                      min={1}
                      max={52}
                      value={durationWeeks}
                      onChange={(e) => setDurationWeeks(e.target.value)}
                      placeholder="e.g. 4"
                      disabled={isGenerating}
                    />
                  </div>
                )}

                {/* Researcher fields */}
                {contentType === "competitive_analysis" && (
                  <div>
                    <Label htmlFor="competitorNames">
                      Known Competitors (comma-separated)
                    </Label>
                    <Input
                      id="competitorNames"
                      value={competitorNames}
                      onChange={(e) => setCompetitorNames(e.target.value)}
                      placeholder="e.g. Acme Corp, BigCo, StartupX"
                      disabled={isGenerating}
                    />
                  </div>
                )}

                {contentType === "market_research" && (
                  <div>
                    <Label htmlFor="researchRegion">Geographic Region</Label>
                    <Input
                      id="researchRegion"
                      value={researchRegion}
                      onChange={(e) => setResearchRegion(e.target.value)}
                      placeholder="e.g. North America, EMEA, Global"
                      disabled={isGenerating}
                    />
                  </div>
                )}

                {contentType === "audience_insight" && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="personaRole">Target Persona Role</Label>
                      <Input
                        id="personaRole"
                        value={personaRole}
                        onChange={(e) => setPersonaRole(e.target.value)}
                        placeholder="e.g. Financial Controller, Head of IT"
                        disabled={isGenerating}
                      />
                    </div>
                    <div>
                      <Label htmlFor="industryVertical">Industry Vertical</Label>
                      <Input
                        id="industryVertical"
                        value={industryVertical}
                        onChange={(e) => setIndustryVertical(e.target.value)}
                        placeholder="e.g. Professional Services, Manufacturing"
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                )}

                {/* Campaign plan fields */}
                {contentType === "campaign_plan" && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="channels">
                        Channels (comma-separated)
                      </Label>
                      <Input
                        id="channels"
                        value={channels}
                        onChange={(e) => setChannels(e.target.value)}
                        placeholder="e.g. email, social, paid search"
                        disabled={isGenerating}
                      />
                    </div>
                    <div>
                      <Label htmlFor="budgetRange">Budget Range</Label>
                      <Input
                        id="budgetRange"
                        value={budgetRange}
                        onChange={(e) => setBudgetRange(e.target.value)}
                        placeholder="e.g. $5k-$10k"
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                )}

                {/* Social calendar fields */}
                {contentType === "social_calendar" && (
                  <div>
                    <Label htmlFor="platforms">
                      Platforms (comma-separated)
                    </Label>
                    <Input
                      id="platforms"
                      value={platforms}
                      onChange={(e) => setPlatforms(e.target.value)}
                      placeholder="e.g. linkedin, twitter, facebook"
                      disabled={isGenerating}
                    />
                  </div>
                )}

                {!brandLoading && !hasBrandProfile && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-medium">Brand Identity required</p>
                      <p className="mt-1 text-amber-700">
                        Configure your company name, brand voice, and value proposition before generating content.{" "}
                        <Link to="/portal/platform/theme" className="font-medium underline">
                          Set up Brand Identity
                        </Link>
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim() || !hasBrandProfile}
                  className="w-full"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Result */}
            {(generatedContent || error) && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Result</CardTitle>
                    <div className="flex items-center gap-2">
                      {generatedContent?.status === "completed" && (
                        <>
                          <Badge variant="outline" className="text-green-600">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            {generatedContent.word_count} words
                          </Badge>
                          {generatedContent.generation_time_seconds && (
                            <Badge variant="outline">
                              {generatedContent.generation_time_seconds}s
                            </Badge>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCopy}
                          >
                            {copied ? (
                              <CheckCircle className="mr-1 h-3 w-3" />
                            ) : (
                              <Copy className="mr-1 h-3 w-3" />
                            )}
                            {copied ? "Copied" : "Copy"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowImprove(!showImprove)}
                          >
                            <Wand2 className="mr-1 h-3 w-3" />
                            Improve
                          </Button>
                        </>
                      )}
                      {generatedContent?.status === "failed" && (
                        <Badge variant="destructive">
                          <XCircle className="mr-1 h-3 w-3" />
                          Failed
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  {/* Improve form */}
                  {showImprove && generatedContent?.status === "completed" && (
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                      <Label htmlFor="improveInstructions" className="text-sm font-medium text-purple-700">
                        Improvement Instructions
                      </Label>
                      <Textarea
                        id="improveInstructions"
                        value={improveInstructions}
                        onChange={(e) => setImproveInstructions(e.target.value)}
                        placeholder="e.g. Make more concise, add statistics, change tone to casual..."
                        rows={2}
                        className="mt-2"
                        disabled={isImproving}
                      />
                      <div className="mt-2 flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleImprove}
                          disabled={isImproving || !improveInstructions.trim()}
                        >
                          {isImproving ? (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          ) : (
                            <Wand2 className="mr-1 h-3 w-3" />
                          )}
                          {isImproving ? "Improving..." : "Apply"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowImprove(false)}
                          disabled={isImproving}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Research sources */}
                  {generatedContent?.research_data && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                      <p className="mb-1 flex items-center gap-1 text-xs font-medium text-blue-700">
                        <Globe className="h-3 w-3" />
                        Web Research Sources
                      </p>
                      <div className="text-xs text-blue-600">
                        {Array.isArray(generatedContent.research_data) ? (
                          <ul className="list-inside list-disc space-y-0.5">
                            {(
                              generatedContent.research_data as unknown as Array<{
                                url?: string;
                                title?: string;
                              }>
                            )
                              .slice(0, 5)
                              .map((source, i) => (
                                <li key={i}>
                                  {source.url ? (
                                    <a
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="underline"
                                    >
                                      {source.title || source.url}
                                    </a>
                                  ) : (
                                    JSON.stringify(source)
                                  )}
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p>Research data included</p>
                        )}
                      </div>
                    </div>
                  )}

                  {generatedContent?.rendered_content && (
                    <div className="prose prose-sm max-w-none rounded-lg border bg-white p-4">
                      <PortalMarkdown content={generatedContent.rendered_content} />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Brand Context Panel */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Brand Context</CardTitle>
                <CardDescription>
                  Loaded from organisation settings. Used to align generated
                  content with your brand.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {brandLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : brandContext?.org_brand_profile ? (
                  <div className="space-y-3 text-sm">
                    <BrandField
                      label="Brand Voice"
                      value={
                        brandContext.org_brand_profile.brand_voice as
                          | string
                          | undefined
                      }
                    />
                    <BrandField
                      label="Tone"
                      value={
                        (
                          brandContext.org_brand_profile.tone_attributes as string[]
                        )?.join(", ")
                      }
                    />
                    <BrandField
                      label="Value Proposition"
                      value={
                        brandContext.org_brand_profile.value_proposition as
                          | string
                          | undefined
                      }
                    />
                    <BrandField
                      label="Key Messages"
                      value={
                        (
                          brandContext.org_brand_profile.key_messages as string[]
                        )?.join(" | ")
                      }
                    />
                    <BrandField
                      label="Differentiators"
                      value={
                        (
                          brandContext.org_brand_profile
                            .key_differentiators as string[]
                        )?.join(", ")
                      }
                    />
                    <BrandField
                      label="Industry Focus"
                      value={
                        (
                          brandContext.org_brand_profile
                            .industry_expertise as string[]
                        )?.join(", ")
                      }
                    />
                    {brandContext.product_lines.length > 0 ? (
                      <BrandField
                        label="Product Lines"
                        value={brandContext.product_lines
                          .map((p) => p.name as string)
                          .join(", ")}
                      />
                    ) : (
                      <div>
                        <span className="font-medium text-slate-700">Product Lines</span>
                        <p className="mt-0.5 text-slate-400">
                          None configured.
                        </p>
                      </div>
                    )}
                    {brandContext.business_verticals.length > 0 ? (
                      <BrandField
                        label="Verticals"
                        value={brandContext.business_verticals
                          .map((v) => v.name as string)
                          .join(", ")}
                      />
                    ) : (
                      <div>
                        <span className="font-medium text-slate-700">Verticals</span>
                        <p className="mt-0.5 text-slate-400">
                          None configured.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3 py-2 text-sm">
                    <div>
                      <span className="font-medium text-slate-700">Brand Identity</span>
                      <p className="mt-0.5 text-slate-400">
                        Not configured.{" "}
                        <Link to="/portal/platform/theme" className="text-primary underline">
                          Set up Brand Identity
                        </Link>
                      </p>
                    </div>
                    {brandContext?.product_lines && brandContext.product_lines.length > 0 ? (
                      <BrandField
                        label="Product Lines"
                        value={brandContext.product_lines
                          .map((p) => p.name as string)
                          .join(", ")}
                      />
                    ) : (
                      <div>
                        <span className="font-medium text-slate-700">Product Lines</span>
                        <p className="mt-0.5 text-slate-400">
                          None configured.
                        </p>
                      </div>
                    )}
                    {brandContext?.business_verticals && brandContext.business_verticals.length > 0 ? (
                      <BrandField
                        label="Verticals"
                        value={brandContext.business_verticals
                          .map((v) => v.name as string)
                          .join(", ")}
                      />
                    ) : (
                      <div>
                        <span className="font-medium text-slate-700">Verticals</span>
                        <p className="mt-0.5 text-slate-400">
                          None configured.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

function BrandField({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  if (!value) return null;
  return (
    <div>
      <span className="font-medium text-slate-700">{label}</span>
      <p className="mt-0.5 text-slate-500">{value}</p>
    </div>
  );
}
